import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableHead, TableRow, Chip, Pagination, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, InputAdornment,
  Tabs, Tab, Alert, LinearProgress,
} from '@mui/material';
import { Add, Search, AccountBalance, WhatsApp, OpenInNew, Upload, Download } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

function CustomerDetailDialog({ customer, open, onClose, dispatch }) {
  const [detail, setDetail] = useState(null);
  const [payAmount, setPayAmount] = useState('');

  useEffect(() => {
    if (open && customer) {
      api.get(`/customers/${customer.id}`).then(r => setDetail(r.data.data));
    }
  }, [open, customer]);

  const recordPayment = async () => {
    try {
      await api.post(`/customers/${customer.id}/payment`, { amount: +payAmount, method: 'cash' });
      dispatch(showSnackbar({ message: 'Payment recorded', severity: 'success' }));
      api.get(`/customers/${customer.id}`).then(r => setDetail(r.data.data));
      setPayAmount('');
    } catch (e) {
      dispatch(showSnackbar({ message: 'Error', severity: 'error' }));
    }
  };

  const [tab, setTab] = useState(0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {customer?.name}
        {detail?.credit_balance > 0 && <Chip label={`Due: ₨ ${fmt(detail.credit_balance)}`} color="error" size="small" sx={{ ml: 1 }} />}
      </DialogTitle>
      <DialogContent>
        {!detail ? <CircularProgress /> : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Ledger" />
              <Tab label="Sales History" />
            </Tabs>

            {tab === 0 && (
              <>
                {detail.credit_balance > 0 && (
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 2 }}>
                    <TextField size="small" label="Payment Amount ₨" type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)} />
                    <Button variant="contained" color="success" onClick={recordPayment} disabled={!payAmount}>Collect Payment</Button>
                  </Box>
                )}
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Date</TableCell><TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell><TableCell align="right">Balance</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {detail.ledger.map(e => (
                      <TableRow key={e.id}>
                        <TableCell>{new Date(e.created_at).toLocaleDateString('en-PK')}</TableCell>
                        <TableCell><Chip label={e.type} size="small" color={e.type === 'debit' ? 'error' : 'success'} /></TableCell>
                        <TableCell align="right">₨ {fmt(e.amount)}</TableCell>
                        <TableCell align="right">₨ {fmt(e.balance_after)}</TableCell>
                        <TableCell>{e.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}

            {tab === 1 && (
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Invoice</TableCell><TableCell align="right">Amount</TableCell>
                  <TableCell>Payment</TableCell><TableCell>Date</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {detail.sales.map(s => (
                    <TableRow key={s.id}>
                      <TableCell><Typography variant="body2" fontWeight={600} color="primary">{s.invoice_number}</Typography></TableCell>
                      <TableCell align="right">₨ {fmt(s.total_amount)}</TableCell>
                      <TableCell><Chip label={s.payment_status} size="small" color={{ paid: 'success', partial: 'warning', credit: 'error' }[s.payment_status]} /></TableCell>
                      <TableCell>{new Date(s.created_at).toLocaleDateString('en-PK')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {detail?.phone && detail?.credit_balance > 0 && (
          <Button variant="outlined" color="success" startIcon={<WhatsApp />}
            onClick={async () => {
              try {
                await api.post(`/whatsapp/payment-reminder/${customer.id}`);
                dispatch(showSnackbar({ message: 'Reminder sent via WhatsApp', severity: 'success' }));
              } catch (e) {
                dispatch(showSnackbar({ message: e.response?.data?.message || 'WhatsApp failed', severity: 'error' }));
              }
            }}>
            Send Reminder
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ImportDialog({ open, onClose, onSuccess, dispatch }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleClose = () => { setFile(null); setResult(null); setError(''); onClose(); };

  const downloadTemplate = async () => {
    const { data } = await api.get('/customers/import/template', { responseType: 'blob' });
    const url = URL.createObjectURL(data);
    const a = document.createElement('a'); a.href = url; a.download = 'customers_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/customers/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data.data);
      dispatch(showSnackbar({ message: `Imported ${data.data.created} customers`, severity: 'success' }));
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || 'Import failed');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Customers from CSV</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✓ Imported: <strong>{result.created}</strong> | Skipped: <strong>{result.skipped}</strong> | Errors: <strong>{result.errors}</strong>
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button variant="outlined" startIcon={<Download />} onClick={downloadTemplate} size="small">
            Download CSV Template
          </Button>
          <Alert severity="info" sx={{ fontSize: 12 }}>
            Columns: <strong>name, phone, email, city, address, credit_limit, opening_balance</strong>
            <br />Phone number must be unique per customer.
          </Alert>
          <Button component="label" variant="outlined" startIcon={<Upload />}>
            {file ? file.name : 'Choose CSV File'}
            <input type="file" accept=".csv" hidden onChange={e => setFile(e.target.files[0])} />
          </Button>
          {loading && <LinearProgress />}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleImport} disabled={!file || loading}>
          {loading ? 'Importing…' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CustomersPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/customers', { params: { page, limit: 15, search } });
      setCustomers(data.data); setTotal(data.pagination.total);
    } finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const openForm = (c = null) => {
    setEditing(c); reset(c || {}); setFormOpen(true);
  };

  const onSubmit = async (data) => {
    try {
      if (editing) await api.put(`/customers/${editing.id}`, data);
      else await api.post('/customers', data);
      dispatch(showSnackbar({ message: editing ? 'Customer updated' : 'Customer added', severity: 'success' }));
      setFormOpen(false); load();
    } catch (e) {
      dispatch(showSnackbar({ message: 'Error', severity: 'error' }));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>{t('nav.customers')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Upload />} onClick={() => setImportOpen(true)}>Import CSV</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => openForm()}>Add Customer</Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <TextField
            placeholder={t('common.search')} size="small" sx={{ mb: 2, width: 320 }}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />

          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('common.phone')}</TableCell>
                <TableCell>{t('common.city')}</TableCell>
                <TableCell align="right">Credit Limit</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {customers.map(c => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}
                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                        onClick={() => { setSelected(c); setDetailOpen(true); }}>
                        {c.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{c.phone}</TableCell>
                    <TableCell>{c.city}</TableCell>
                    <TableCell align="right">₨ {fmt(c.credit_limit)}</TableCell>
                    <TableCell align="right">
                      {c.credit_balance > 0
                        ? <Chip label={`₨ ${fmt(c.credit_balance)}`} size="small" color="error" icon={<AccountBalance />} />
                        : <Chip label="Clear" size="small" color="success" />}
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => openForm(c)}>Edit</Button>
                      <Button size="small" startIcon={<OpenInNew />} onClick={() => navigate(`/customers/${c.id}`)}>View</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(total / 15)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>

      {/* Add/Edit dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
        <DialogContent>
          <Box component="form" id="cust-form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}><TextField fullWidth label="Name *" {...register('name', { required: true })} error={!!errors.name} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Phone" {...register('phone')} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Email" {...register('email')} /></Grid>
              <Grid item xs={8}><TextField fullWidth label="Address" {...register('address')} /></Grid>
              <Grid item xs={4}><TextField fullWidth label="City" {...register('city')} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Credit Limit ₨" type="number" {...register('credit_limit')} /></Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" type="submit" form="cust-form">{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Customer detail dialog */}
      <CustomerDetailDialog customer={selected} open={detailOpen} onClose={() => setDetailOpen(false)} dispatch={dispatch} />

      {/* Import dialog */}
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onSuccess={load} dispatch={dispatch} />
    </Box>
  );
}
